import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { Container, PrimaryButton, SimpleLoader, Steps, TabsPanel } from '@dev/ee-components';
import { FormSubtypeEnum } from '@dev/ee-api/ee-api-models';
import { useExitModal } from '@/services/hooks/useExitModal';
import { FormsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { GetContactFieldsStart, getCustomFieldsStore } from '@/store/customFields/actions';
import { LoadStart, Reset, getFormCreatorStore, UpdateSaveSettings } from '@/store/formCreator/actions';
import { HistoryBackActionContext, useHistoryBackAction } from '@/services/hooks/useHistoryBackAction';
import { MergeFieldsLoadStart, getMergeFieldsStore } from '@/store/mergeFields/actions';
import { MergeFieldsStateStatus } from '@/store/mergeFields/types';
import { TemplateEditorModal } from '@/components/FormCreator/components/TemplateEditorModal';
import { useSteps } from '@/components/FormCreator/hooks/useSteps';
import { FormCreatorStatus } from '@/store/formCreator/types';
import { PreviewOverlay } from './components/PreviewOverlay';

export const FormCreator = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const route = useRouteMatch();
    const { name } = useParams<{ name: string }>();
    const isSavedRef = React.useRef(true);
    const { isTemplateOpen, formObject, status, saveSettings } = useSelector(getFormCreatorStore);
    const { status: mergeFieldsStatus, data: mergeFields } = useSelector(getMergeFieldsStore);
    const { isLoadingFields, isLoadedFields } = useSelector(getCustomFieldsStore);

    const switchCreatorMode = (): void => { dispatch(UpdateSaveSettings({ isGalleryCreator: !saveSettings?.isGalleryCreator })); };
    const switchCreatorPublicTemplate = (): void => { dispatch(UpdateSaveSettings({ isPublic: !saveSettings?.isPublic })); };
    const switchCreatorDissmisButtonVisibility = (): void => { dispatch(UpdateSaveSettings({ isDismissButtonVisible: !saveSettings?.isDismissButtonVisible })); };
    const setCustomScreenshot = (e): void => {
        const file = e.target.files[0];
        if (file && (file.type === 'image/jpeg' || file.type === 'image/png')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const customScreenshotBase64 = reader.result as string;
                dispatch(UpdateSaveSettings({ customScreenshot: customScreenshotBase64 })); 
            };
            reader.readAsDataURL(file)
        }
    };
    const isPopupCreator = route.path.includes(FormsPaths.FormPopupCreator.replace(`${pathsParams.nameOptional}`, '')) || formObject?.FormSubtype === FormSubtypeEnum.Popup;

    const historyBackAction = useHistoryBackAction({
        isSavedRef,
        exitModalTitle: t('exitformcreator'),
        exitModalBody: t('exitcreatorwithoutsaving'),
    });
    const isTemplateEditorReady = mergeFieldsStatus === MergeFieldsStateStatus.LOADED || !!mergeFields;

    const { boot, cleanup, handleExit } = useExitModal(
        isSavedRef,
        FormsPaths.FormList,
        { title: t('exitformcreator') },
        t('exitcreatorwithoutsaving')
    );

    useEffect(() => {
        boot();
        dispatch(MergeFieldsLoadStart());
        if (!isLoadingFields && !isLoadedFields) {
            dispatch(GetContactFieldsStart({ isUrlUpdateDisabled: true }))
        }
        return () => {
            dispatch(Reset());
            cleanup();
        };
    }, []);
    
    useEffect((): void => {
        if (!name) return;
        dispatch(LoadStart(safelyDecodeBase64Url(name)));
    }, []);
    
    useEffect((): void => {
        if (!formObject?.Name) return;
        const url = (isPopupCreator ? FormsPaths.FormPopupCreator : FormsPaths.FormCreator).replace(`${pathsParams.nameOptional}`, safelyEncodeBase64Url(formObject.Name));
        historyBackAction.updatePathSafely(url);
    }, [formObject]);

    const onExit = (): void => handleExit(null, true);
    const steps = useSteps({onExit, isSavedRef, isPopupCreator});

    if (status === FormCreatorStatus.LOADING || name && status === FormCreatorStatus.DEFAULT) {
        return <Container><SimpleLoader /></Container>;
    }

    return (
        <Container>
            <TabsPanel
                breadcrumbs={t('forms')}
                breadcrumbsOnClick={onExit}
                title={isPopupCreator ? t('popupform') : t('inlineform')}
                disableSentenceCase
            />
            {GALLERY_CREATOR==="true" ? (<div style={{marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '20px'}}>
                <PrimaryButton onClick={switchCreatorMode} text={saveSettings?.isGalleryCreator ? 'Editing as creator now' : 'Editing as simple user now'} />
                <PrimaryButton onClick={switchCreatorPublicTemplate} text={saveSettings?.isPublic ? 'saving as public' : 'saving as private'} disabled={!saveSettings?.isGalleryCreator} />
                <PrimaryButton onClick={switchCreatorDissmisButtonVisibility} text={saveSettings?.isDismissButtonVisible ? 'close button visible' : 'close button hidden'} disabled={!saveSettings?.isGalleryCreator} />
                <div>Override auto screenshot: <input type="file" accept=".jpg,.jpeg,.png" onChange={setCustomScreenshot} disabled={!saveSettings?.isGalleryCreator} /></div>
            </div>) : null}
            <HistoryBackActionContext.Provider value={historyBackAction}>
                <Steps steps={isTemplateEditorReady ? steps : []} activeStep={0} />
                {isTemplateOpen && <TemplateEditorModal isPopupCreator={isPopupCreator} isDismissButtonVisible={saveSettings?.isDismissButtonVisible} />}
                <PreviewOverlay />
            </HistoryBackActionContext.Provider>
        </Container>
    );
};
